import {
  Grid,
  Heading,
  Skeleton,
  CardBody,
  Card,
  Button,
  Flex,
  VStack,
  CircularProgress,
  Text,
} from '@chakra-ui/react';
import range from 'lodash/range';
import { memo } from 'react';
import {
  getEnvironmentFromUrl,
  getInventoryEnvironmentLabel,
} from 'features/context-ids/utils/environmentUtils';
import { AppTile } from 'features/home/components/homepage/components/AppTile';
import NoTenantsPanelMessage from './NoTenantsPanelMessage';
import { env } from 'features/common/config/envConfig';
import { T } from '@transifex/react';
import {
  ContextUserApplication,
  useGetTenantsQuery,
  useGetGoLiveDateQuery,
} from 'gql/graphql';
import { selectApp, useMainStateDispatch } from 'features/main/context';
import { openDrawer } from 'features/main/context/drawer/actionCreators';
import { isNonProdEnvironment } from '@sitecore-ui/portal-singular';
import { fetchLastSuccessfulLoginOrganizationId } from 'features/common/auth/auth0Storage';
import { useGetTenantsByOrgAndProductCode } from 'features/common/services/inventory/tenants';
import { useFeature } from 'lib/featureFlags/features';
import { isHiddenDamTenant } from 'features/common/utils/tenantLabels';

const TenantsFeed = () => {
  const organizationId = fetchLastSuccessfulLoginOrganizationId() ?? '';
  const {
    data: activatingTenantsData,
    isLoading: activatingLoading,
    isSuccess: activatingSuccess,
  } = useGetTenantsByOrgAndProductCode({
    organizationId,
    productCode: 'XMCloud',
    state: 'Activating',
  });
  const activatingItems = activatingTenantsData?.data ?? [];
  const currentEnv = getEnvironmentFromUrl();
  const expectedInventoryLabel = getInventoryEnvironmentLabel(currentEnv);
  const filteredActivatingItems = activatingItems.filter(
    (item) => item.labels?.Environment === expectedInventoryLabel,
  );

  const [, dispatch] = useMainStateDispatch();

  const goLivePlanningEnabled = useFeature('GO_LIVE_PLANNING');
  const { data: goLiveDateData } = useGetGoLiveDateQuery({
    skip: !goLivePlanningEnabled,
  });
  const goLiveDate =
    goLiveDateData?.serviceNow?.goLiveDate?.goLiveDate ?? undefined;

  const {
    data: tenantsQueryData,
    loading: tenantsQueryLoading,
    fetchMore,
  } = useGetTenantsQuery({
    variables: {
      first: 20,
      env,
    },
    nextFetchPolicy: 'cache-only',
  });

  const showActivating =
    activatingSuccess &&
    !activatingLoading &&
    !tenantsQueryLoading &&
    filteredActivatingItems.length > 0;

  const edges = tenantsQueryData?.user?.applications?.edges ?? [];
  const nodes = tenantsQueryData?.user?.applications?.nodes ?? [];

  const getCustomerEnvironmentType = (id: string) =>
    nodes
      .filter((node) => node.id === id)[0]
      ?.labels?.filter(
        (label) =>
          label.key === 'CustomerEnvironmentType' &&
          !isNonProdEnvironment(label.value),
      )[0]?.value;

  const getSitecorePlanId = (id: string) =>
    nodes
      .filter((node) => node.id === id)[0]
      ?.labels?.find((label) => label.key === 'SitecorePlanId')?.value;

  const applicationsViews = edges
    .filter(
      (edge) =>
        !isHiddenDamTenant(
          nodes.find((node) => node.id === edge.node.id)?.labels,
        ),
    )
    .map((edge) => {
      const { id, appearance } = edge.node;
      const aNode = appearance?.web?.actions?.nodes?.[0];
      // Without appearance, fall back to the application's own name and a default icon.
      const application = nodes.find((node) => node.id === id);

      return {
        displayName: aNode?.displayName ?? application?.displayName,
        name: aNode?.name ?? application?.name ?? '',
        id,
        icon: aNode?.icon,
        customerEnvironmentType: getCustomerEnvironmentType(id),
        sitecorePlanId: getSitecorePlanId(id),
      };
    });

  const fetchMoreTenants = () => {
    const { endCursor } = tenantsQueryData?.user?.applications?.pageInfo || {};

    return fetchMore({
      variables: {
        after: endCursor,
      },
      updateQuery: (_, { fetchMoreResult }) => {
        if (!fetchMoreResult)
          return tenantsQueryData || { user: { __typename: 'ContextUser' } };

        const pageInfo = fetchMoreResult?.user?.applications?.pageInfo || {
          hasNextPage: false,
          hasPreviousPage: false,
          startCursor: null,
          endCursor: null,
        };

        return {
          user: {
            ...fetchMoreResult.user,
            applications: {
              ...fetchMoreResult.user?.applications,
              edges: [
                ...(tenantsQueryData?.user?.applications?.edges ?? []),
                ...(fetchMoreResult?.user?.applications?.edges ?? []),
              ],
              nodes: [
                ...(tenantsQueryData?.user?.applications?.nodes ?? []),
                ...(fetchMoreResult?.user?.applications?.nodes ?? []),
              ],
              pageInfo,
              totalCount: fetchMoreResult?.user?.applications?.totalCount || 0,
            },
          },
        };
      },
    });
  };

  const cardOnClick = (tenantId: string) => {
    dispatch(
      selectApp({
        app: nodes.find(
          (node) => node.id === tenantId,
        ) as ContextUserApplication,
      }),
    );
    dispatch(openDrawer('tenant-drawer'));
  };

  const shouldShowMoreButton =
    applicationsViews.length > 0 &&
    !tenantsQueryLoading &&
    (tenantsQueryData?.user?.applications?.pageInfo?.hasNextPage ?? false);

  const noApps: boolean =
    !activatingLoading &&
    activatingItems.length === 0 &&
    !tenantsQueryLoading &&
    applicationsViews.length === 0;
  return (
    <>
      <Heading size='md' noOfLines={1}>
        Sitecore Cloud
      </Heading>
      <Card
        variant='flat'
        size={['md', 'lg']}
        w='full'
        data-testid='apps-block'
        data-behavior-analytics-feature='apps - home page'
      >
        <CardBody>
          {tenantsQueryLoading ? (
            <Grid
              gap='4'
              templateColumns='repeat(auto-fill, minmax(var(--sizes-40), 1fr))'
            >
              {range(10).map((_, index) => (
                <Skeleton
                  h='32'
                  w='40'
                  isLoaded={false}
                  key={index}
                  data-testid='app-skeleton-placeholder'
                />
              ))}
            </Grid>
          ) : (
            <>
              {noApps ? (
                <NoTenantsPanelMessage />
              ) : (
                <Grid
                  columnGap='0'
                  rowGap='4'
                  templateColumns={[
                    'repeat(auto-fill, minmax(var(--sizes-32), 1fr))',
                    'repeat(auto-fill, minmax(var(--sizes-44), 1fr))',
                  ]}
                >
                  {showActivating &&
                    filteredActivatingItems.map((item) => (
                      <VStack
                        key={item.id}
                        p='4'
                        cursor='pointer'
                        _hover={{ backgroundColor: 'neutral.100' }}
                        _active={{ backgroundColor: 'neutral.200' }}
                        borderRadius='md'
                        transitionProperty='common'
                        transitionDuration='normal'
                        alignItems='center'
                        justifyContent='flex-start'
                      >
                        <CircularProgress
                          size='2.3rem'
                          isIndeterminate
                          color='primary'
                          thickness='8px'
                          mb='1'
                        />
                        <Text
                          fontWeight='medium'
                          textAlign='center'
                          w='full'
                          noOfLines={1}
                          wordBreak='break-all'
                        >
                          Building in progress
                        </Text>
                      </VStack>
                    ))}
                  {applicationsViews.map(
                    (
                      {
                        displayName,
                        name,
                        icon,
                        id,
                        customerEnvironmentType,
                        sitecorePlanId,
                      },
                      idx,
                    ) => {
                      const subTitleNoSpaces = displayName
                        ? displayName.replaceAll(' ', '-')
                        : '';

                      return (
                        <AppTile
                          key={`${name}_${subTitleNoSpaces}_${idx}`}
                          data-testid={`app-card-${idx}`}
                          data-behavior-analytics-id={`app-card_ga_${name}_${subTitleNoSpaces}`}
                          appName={name}
                          tenantName={displayName || ''}
                          onClick={() => cardOnClick(id)}
                          {...(icon?.src && { image: icon.src })}
                          customerEnvironmentType={customerEnvironmentType}
                          sitecorePlanId={sitecorePlanId}
                          goLiveDate={name.includes('SitecoreAI') && goLiveDate}
                        />
                      );
                    },
                  )}
                </Grid>
              )}
              {shouldShowMoreButton && (
                <Flex align='center' justifyContent='center'>
                  <Button
                    variant='link'
                    mt='6'
                    onClick={fetchMoreTenants}
                    data-testid='show-all-button'
                  >
                    <T _str='Show more' />
                  </Button>
                </Flex>
              )}
            </>
          )}
        </CardBody>
      </Card>
    </>
  );
};

export const TenantsPanel = memo(TenantsFeed);
